import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Switch } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import LoggedInLayout from "../layout";
import Login from "../pages/Login/";
import Signup from "../pages/Signup";
import LandingPage from "../pages/LandingPage";
import { AuthProvider } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { TelegramProvider } from "../context/Telegram/TelegramContext";
import Route from "./Route";

// Componente de loading
const Loading = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

// Lazy loading de componentes pesados
const Dashboard = lazy(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = lazy(() => import("../pages/TicketResponsiveContainer"));
const Connections = lazy(() => import("../pages/Connections/"));
const Settings = lazy(() => import("../pages/Settings/"));
const Financeiro = lazy(() => import("../pages/Financeiro/"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const ContactImportPage = lazy(() => import("../pages/Contacts/import"));
const ChatMoments = lazy(() => import("../pages/Moments"));
const Queues = lazy(() => import("../pages/Queues/"));
const Tags = lazy(() => import("../pages/Tags/"));
const MessagesAPI = lazy(() => import("../pages/MessagesAPI/"));
const Helps = lazy(() => import("../pages/Helps/"));
const ContactLists = lazy(() => import("../pages/ContactLists/"));
const ContactListItems = lazy(() => import("../pages/ContactListItems/"));
const Companies = lazy(() => import("../pages/Companies/"));
const MiServer = lazy(() => import("../pages/MiServer/"));
const Geolocation = lazy(() => import("../pages/Geolocation/"));
const Wallets = lazy(() => import("../pages/Wallets/"));
const SmtpConfig = lazy(() => import("../pages/EmailCampaigns/SmtpConfig"));
const EmailList = lazy(() => import("../pages/EmailCampaigns/EmailList"));
const SendEmail = lazy(() => import("../pages/EmailCampaigns/SendEmail"));
const CampaignHistory = lazy(() => import("../pages/EmailCampaigns/CampaignHistory"));
const TwilioConfig = lazy(() => import("../pages/Twilio/TwilioConfig"));
const PhoneNumberList = lazy(() => import("../pages/Twilio/PhoneNumberList"));
const SendSms = lazy(() => import("../pages/Twilio/SendSms"));
const SmsHistory = lazy(() => import("../pages/Twilio/SmsHistory"));
const MakeCall = lazy(() => import("../pages/Twilio/MakeCall"));
const CallHistory = lazy(() => import("../pages/Twilio/CallHistory"));
const LiveCalls = lazy(() => import("../pages/Twilio/LiveCalls"));
const SendGridConfig = lazy(() => import("../pages/SendGrid/SendGridConfig"));
const SendGridSendEmail = lazy(() => import("../pages/SendGrid/SendGridSendEmail"));
const SendGridHistory = lazy(() => import("../pages/SendGrid/SendGridHistory"));
const SendGridTemplates = lazy(() => import("../pages/SendGrid/SendGridTemplates"));
const NewChat = lazy(() => import("../pages/NewChat"));
const WhatsAppLinks = lazy(() => import("../pages/WhatsAppLinks"));
const TelegramTickets = lazy(() => import("../pages/TelegramTickets"));
const NewTelegramChat = lazy(() => import("../pages/NewTelegramChat"));
const QuickMessages = lazy(() => import("../pages/QuickMessages/"));
const Schedules = lazy(() => import("../pages/Schedules"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const CampaignsConfig = lazy(() => import("../pages/CampaignsConfig"));
const CampaignReport = lazy(() => import("../pages/CampaignReport"));
const Annoucements = lazy(() => import("../pages/Annoucements"));
const Chat = lazy(() => import("../pages/Chat"));
const Prompts = lazy(() => import("../pages/Prompts"));
const AllConnections = lazy(() => import("../pages/AllConnections/"));
const Reports = lazy(() => import("../pages/Reports"));
const RelatorioVendas = lazy(() => import("../pages/RelatorioVendas"));
const Subscription = lazy(() => import("../pages/Subscription/"));
const QueueIntegration = lazy(() => import("../pages/QueueIntegration"));
const Files = lazy(() => import("../pages/Files/"));
const ToDoList = lazy(() => import("../pages/ToDoList/"));
const Kanban = lazy(() => import("../pages/Kanban"));
const TagsKanban = lazy(() => import("../pages/TagsKanban"));
const BirthdaySettingsPage = lazy(() => import("../pages/BirthdaySettings"));

// Lazy loading para Flow Builder
const FlowBuilderConfig = lazy(() => import("../pages/FlowBuilderConfig").then(module => ({ default: module.FlowBuilderConfig })));
const FlowBuilder = lazy(() => import("../pages/FlowBuilder"));
const FlowDefault = lazy(() => import("../pages/FlowDefault"));
const CampaignsPhrase = lazy(() => import("../pages/CampaignsPhrase"));

const Routes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <TicketsContextProvider>
          <Suspense fallback={<Loading />}>
            <Switch>
            <Route exact path="/" component={LandingPage} />
            <Route exact path="/login" component={Login} />
            <Route exact path="/signup" component={Signup} />
            <WhatsAppsProvider>
              <TelegramProvider>
                <LoggedInLayout>
                <Route
                  exact
                  path="/financeiro"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/financeiro-aberto"
                  component={Financeiro}
                  isPrivate
                />

                <Route
                  exact
                  path="/companies"
                  component={Companies}
                  isPrivate
                />
                <Route
                  exact
                  path="/geolocation"
                  component={Geolocation}
                  isPrivate
                />
                <Route
                  exact
                  path="/mi-server"
                  component={MiServer}
                  isPrivate
                />
                <Route
                  exact
                  path="/email-campaigns/smtp-config"
                  component={SmtpConfig}
                  isPrivate
                />
                <Route
                  exact
                  path="/email-campaigns/emails"
                  component={EmailList}
                  isPrivate
                />
                <Route
                  exact
                  path="/email-campaigns/send"
                  component={SendEmail}
                  isPrivate
                />
                <Route
                  exact
                  path="/email-campaigns/history"
                  component={CampaignHistory}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/config"
                  component={TwilioConfig}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/phone-numbers"
                  component={PhoneNumberList}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/sms/send"
                  component={SendSms}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/sms/history"
                  component={SmsHistory}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/calls/make"
                  component={MakeCall}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/calls/history"
                  component={CallHistory}
                  isPrivate
                />
                <Route
                  exact
                  path="/twilio/calls/live"
                  component={LiveCalls}
                  isPrivate
                />
                <Route
                  exact
                  path="/sendgrid-config"
                  component={SendGridConfig}
                  isPrivate
                />
                <Route
                  exact
                  path="/sendgrid-send"
                  component={SendGridSendEmail}
                  isPrivate
                />
                <Route
                  exact
                  path="/sendgrid-history"
                  component={SendGridHistory}
                  isPrivate
                />
                <Route
                  exact
                  path="/sendgrid-templates"
                  component={SendGridTemplates}
                  isPrivate
                />
                <Route
                  exact
                  path="/new-chat"
                  component={NewChat}
                  isPrivate
                />
                <Route
                  exact
                  path="/whatsapp-links"
                  component={WhatsAppLinks}
                  isPrivate
                />
                <Route
                  exact
                  path="/ticketst/:ticketId?"
                  component={TelegramTickets}
                  isPrivate
                />
                <Route
                  exact
                  path="/new-telegram-chat"
                  component={NewTelegramChat}
                  isPrivate
                />
                <Route
                  exact
                  path="/birthday-settings"
                  component={BirthdaySettingsPage}
                  isPrivate
                />
                <Route exact path="/dashboard" component={Dashboard} isPrivate />
                <Route
                  exact
                  path="/tickets/:ticketId?"
                  component={TicketResponsiveContainer}
                  isPrivate
                />
                <Route
                  exact
                  path="/connections"
                  component={Connections}
                  isPrivate
                />
                <Route
                  exact
                  path="/quick-messages"
                  component={QuickMessages}
                  isPrivate
                />
                <Route exact path="/todolist" component={ToDoList} isPrivate />
                <Route
                  exact
                  path="/schedules"
                  component={Schedules}
                  isPrivate
                />
                <Route exact path="/tags" component={Tags} isPrivate />
                <Route exact path="/contacts" component={Contacts} isPrivate />
                <Route
                  exact
                  path="/contacts/import"
                  component={ContactImportPage}
                  isPrivate
                />
                <Route exact path="/wallets" component={Wallets} isPrivate />
                <Route exact path="/helps" component={Helps} isPrivate />
                <Route exact path="/users" component={Users} isPrivate />
                <Route
                  exact
                  path="/messages-api"
                  component={MessagesAPI}
                  isPrivate
                />
                <Route
                  exact
                  path="/settings"
                  component={Settings}
                  isPrivate
                />
                <Route exact path="/queues" component={Queues} isPrivate />
                <Route exact path="/reports" component={Reports} isPrivate />
                <Route
                  exact
                  path="/relatorio-vendas"
                  component={RelatorioVendas}
                  isPrivate
                />
                <Route
                  exact
                  path="/queue-integration"
                  component={QueueIntegration}
                  isPrivate
                />
                <Route
                  exact
                  path="/announcements"
                  component={Annoucements}
                  isPrivate
                />
                <Route exact path="/chats/:id?" component={Chat} isPrivate />
                <Route exact path="/files" component={Files} isPrivate />
                <Route
                  exact
                  path="/moments"
                  component={ChatMoments}
                  isPrivate
                />
                <Route exact path="/Kanban" component={Kanban} isPrivate />
                <Route
                  exact
                  path="/TagsKanban"
                  component={TagsKanban}
                  isPrivate
                />
                <Route exact path="/prompts" component={Prompts} isPrivate />
                <Route
                  exact
                  path="/allConnections"
                  component={AllConnections}
                  isPrivate
                />

                <Route
                  exact
                  path="/phrase-lists"
                  component={CampaignsPhrase}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilders"
                  component={FlowBuilder}
                  isPrivate
                />
                <Route
                  exact
                  path="/flowbuilder/:id?"
                  component={FlowBuilderConfig}
                  isPrivate
                />

                {showCampaigns && (
                  <>
                    <Route
                      exact
                      path="/contact-lists"
                      component={ContactLists}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/contact-lists/:contactListId/contacts"
                      component={ContactListItems}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns"
                      component={Campaigns}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaign/:campaignId/report"
                      component={CampaignReport}
                      isPrivate
                    />
                    <Route
                      exact
                      path="/campaigns-config"
                      component={CampaignsConfig}
                      isPrivate
                    />
                  </>
                )}
              </LoggedInLayout>
              </TelegramProvider>
            </WhatsAppsProvider>
            </Switch>
          </Suspense>
          <ToastContainer position="top-center" autoClose={3000} />
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
